// Prompt-ready rendering of a member plan's chosen themes (prompt-context
// integration contract): the journey composer's weeklyPlan input port and the
// voice-session weekly-plan block both render the SAME derivation, so the two
// prompt surfaces cannot drift apart. Pure and clock-free by design — callers
// hand in the member-local day key; this module never reads a wall clock, so
// repo-layer readers can consume it without importing a service.

import { DateTime } from 'luxon';
import { boundText } from '../prompts/bounds';
import {
  WEEKLY_PLAN_BLURB_MAX_CHARS,
  WEEKLY_PLAN_MAX_SELECTION,
  WEEKLY_PLAN_TITLE_MAX_CHARS,
} from '../weekly-plan/schema';
import { intakeFocusConfigSchema } from './intake-capabilities';
import { aiThemeConfigSchema } from './schema';

/** The plan-row slice the derivation needs — a subset of the Drizzle select shape. */
export interface PlanThemeContextPlan {
  /** Member-local start date (YYYY-MM-DD, inclusive). */
  startsOn: string;
  /** Member-local end date (YYYY-MM-DD, exclusive); null for the starter plan, which has no end. */
  endsOn: string | null;
  /** The plan's frozen IANA zone — all window arithmetic happens in it. */
  timezone: string;
}

/** The item-row slice the derivation needs. */
export interface PlanThemeContextItem {
  itemType: string;
  /**
   * Only AI-origin themes are weekly-review focus material; member- and
   * care-plan-origin items are out of this feed's scope and skip silently.
   */
  origin: string;
  /** Stored jsonb — parsed here through the shared schema, never cast. */
  config: unknown;
}

export interface PlanThemeContext {
  /** True iff at least one valid theme rendered. */
  present: boolean;
  /** Window-framing line first, then one bounded line per theme; empty when absent. */
  lines: string[];
  /** Theme rows whose config failed the shared schema — for the caller's structural warn. */
  invalidThemeCount: number;
}

const ABSENT_CONTEXT: Omit<PlanThemeContext, 'invalidThemeCount'> = {
  present: false,
  lines: [],
};

/**
 * Window framing derived entirely from the stored dates in the plan's own
 * zone. The day position is only claimed while `dayKey` sits inside the
 * half-open window; outside it the line says so instead of fabricating a
 * day number (a completion crossing local midnight, or an ended-but-unreviewed
 * plan, are both real shapes).
 */
function windowFramingLine(plan: PlanThemeContextPlan, dayKey: string): string | null {
  const start = DateTime.fromISO(plan.startsOn, { zone: plan.timezone });
  const day = DateTime.fromISO(dayKey, { zone: plan.timezone });
  if (!start.isValid || !day.isValid) return null;
  if (day.startOf('day') < start.startOf('day')) {
    return `The member's chosen focus for their upcoming plan period (starts ${plan.startsOn}):`;
  }
  if (plan.endsOn === null) {
    // The starter plan has no end, so there is no "day N of M" to claim — only
    // how far in the member is.
    const dayN = Math.round(day.startOf('day').diff(start.startOf('day'), 'days').days) + 1;
    return `The member's chosen focus for their starter plan (day ${dayN}, no fixed end):`;
  }
  const end = DateTime.fromISO(plan.endsOn, { zone: plan.timezone });
  if (!end.isValid) return null;
  const totalDays = Math.round(end.startOf('day').diff(start.startOf('day'), 'days').days);
  if (totalDays <= 0) return null;
  if (day.startOf('day') >= end.startOf('day')) {
    return `The member's chosen focus from their current plan period (the period has ended; a review is due):`;
  }
  const dayN = Math.round(day.startOf('day').diff(start.startOf('day'), 'days').days) + 1;
  return `The member's chosen focus for this plan period (day ${dayN} of ${totalDays}):`;
}

/**
 * Render a plan's theme items as prompt lines. Fail-soft contract: an
 * unrenderable window or zero valid themes resolves `present: false` with the
 * invalid count preserved so callers can warn structurally; this function
 * never throws on stored data.
 */
export function derivePlanThemeContext(
  plan: PlanThemeContextPlan,
  items: readonly PlanThemeContextItem[],
  dayKey: string,
): PlanThemeContext {
  let invalidThemeCount = 0;
  const themes: string[] = [];
  for (const item of items) {
    if (
      item.itemType !== 'theme' ||
      (item.origin !== 'ai' && !(plan.endsOn === null && item.origin === 'member'))
    )
      continue;
    const parsed = aiThemeConfigSchema.safeParse(item.config);
    const initial = plan.endsOn === null ? intakeFocusConfigSchema.safeParse(item.config) : null;
    const focus = parsed.success
      ? { title: parsed.data.title, blurb: parsed.data.blurb }
      : initial?.success
        ? { title: initial.data.title, blurb: initial.data.description }
        : null;
    if (!focus) {
      invalidThemeCount += 1;
      continue;
    }
    if (themes.length >= WEEKLY_PLAN_MAX_SELECTION) continue;
    const title = boundText(focus.title, WEEKLY_PLAN_TITLE_MAX_CHARS);
    const blurb = boundText(focus.blurb, WEEKLY_PLAN_BLURB_MAX_CHARS);
    if (title.length === 0) continue;
    themes.push(blurb.length > 0 ? `${title} — ${blurb}` : title);
  }
  if (themes.length === 0) return { ...ABSENT_CONTEXT, invalidThemeCount };
  const framing = windowFramingLine(plan, dayKey);
  if (framing === null) return { ...ABSENT_CONTEXT, invalidThemeCount };
  return { present: true, lines: [framing, ...themes], invalidThemeCount };
}
